/**
 * The help text behind the small `i` on every Change Events screen.
 *
 * The wording here is the agreed wording. It was checked against the code, so
 * change it only with the same care. Never put raw HTML in this file: each
 * section is data, and HelpButton.vue decides how it is drawn.
 *
 * A section is one of three shapes:
 *   { heading, paragraphs: [ 'text' | { strong: 'lead', text: 'rest' } ] }
 *   { heading, bullets: [ { term, text } ] }
 *   { heading, table: { columns: [ 'a', 'b' ], rows: [ [ 'a', 'b' ] ] } }
 *
 * A section may also carry `footnotes`, drawn as plain paragraphs after the
 * bullets or the table.
 *
 * Where this file explains a set of values, it builds the rows from the same
 * constants the screens use. A hand-written copy drifts: the "Why Grouped"
 * table here still named rules the screens had already renamed.
 */

import { joinRuleHelpItems, changeTypeHelpItems } from './ChangeEvents.consts';

const OVERVIEW = {
  title: 'Overview',
  sections: [
    {
      heading: 'What this page shows',
      paragraphs: [
        'WebOrion Monitor raises one alert for each changed page. When one thing changes across a whole website, that becomes hundreds of alerts, and the alerts that matter get lost.',
        'This page shows what happens when those alerts are grouped by what caused them.'
      ]
    },
    {
      heading: 'The four boxes',
      paragraphs: ['They read left to right, and they are two separate ideas:'],
      bullets: [
        { term: 'Alerts and Change events', text: 'are about the dashboard. They say how many rows a person has to read.' },
        { term: 'Site incidents and Messages', text: 'are about the inbox. They say how many messages get sent.' }
      ],
      footnotes: [
        'The sending rules never look at the groups. They only count alerts for each website. Mixing these two up is the easiest way to misread this page.'
      ]
    },
    {
      heading: 'Alert Coverage',
      paragraphs: [
        'Grouping never deletes an alert. Every change event opens back up into the alerts inside it, and a serious alert always makes its whole group serious, so it can never be hidden inside a quieter row.'
      ]
    }
  ]
};

const CHANGE_EVENTS = {
  title: 'Aggregated Alerts',
  sections: [
    {
      heading: 'Definition',
      paragraphs: [
        'A grouped alert holds the alerts that came from one change. One website update can change many pages, and each page raises its own alert. All those alerts are grouped in one grouped alert.'
      ]
    },
    {
      heading: 'Grouping Method',
      paragraphs: [
        'An alert lists every file that changed. On a busy website, most of those files change every day. The system ignores them. It keeps the files that are new and unusual for that website. Alerts that share those files go into one group.',
        'A group closes after the quiet period. It does not reopen. A later alert starts a new group.'
      ]
    },
    {
      heading: 'Change Types',
      table: {
        columns: ['Change Type', 'Meaning'],
        rows: changeTypeHelpItems().map((item) => [item.name, item.meaning])
      },
      footnotes: ['A group covers one website only. It never spans more than one website.']
    }
  ]
};

const CHANGE_EVENT_DETAIL = {
  title: 'Aggregated Alert Detail',
  sections: [
    {
      heading: 'Origin',
      paragraphs: ['Every change event has a first alert. That alert is how the event is identified, so it never changes once the event exists.']
    },
    {
      heading: 'The Files That Caused This Change',
      paragraphs: [
        'An alert lists every file that differs from the baseline. This list is shorter. It holds only the files that were new for that page and unusual for that website, because those are the probable cause.',
        'An empty list means no single file stands out. The change matched what this website changes every day.'
      ]
    },
    {
      heading: 'Why Grouped',
      table: {
        columns: ['Reason', 'Meaning'],
        rows: joinRuleHelpItems().map((item) => [item.name, item.meaning])
      }
    }
  ]
};

const SITE_INCIDENTS = {
  title: 'Site-Wide Incidents',
  sections: [
    {
      heading: 'Definition',
      paragraphs: [
        'A site-wide incident opens when a burst of HIGH or MEDIUM alerts of the same type arrives. The two types are potential defacement and site unreachable. The count is made for each FQDN (Fully Qualified Domain Name). An FQDN is one website address, such as www.example.com.'
      ]
    },
    // "When one opens" was removed. The section above now says when a window
    // opens, and the band over the table gives the live numbers. Two places
    // that say the same thing can disagree.
    {
      heading: 'Incident Types',
      bullets: [
        { term: 'Potential Site-Wide Defacement', text: 'Alerts with suspicious or malicious changes.' },
        { term: 'Site Unreachable', text: 'The pages in the website are not reachable.' }
      ],
      footnotes: ['The two types are counted separately. One website can hold one of each at the same time.']
    },
    {
      heading: 'Window Closure',
      paragraphs: [
        'A window closes automatically when no alert of the same type arrives for the configured period. A user can also close a window manually. The system sends a message when a window closes.',
        {
          strong: 'No open site-wide incidents is the normal condition.',
          text: 'It does not indicate a fault.'
        }
      ]
    }
  ]
};

const SITE_INCIDENT_DETAIL = {
  title: 'Site-Wide Incident Detail',
  sections: [
    {
      heading: 'Timeline',
      paragraphs: [
        'Each row is read from the record. The alerts that arrived before the window opened were emailed separately, in full.'
      ]
    },
    {
      heading: 'Alert Arrivals',
      paragraphs: [
        'Each bar counts the alerts that arrived in one slice of the window. It shows the shape of the burst.'
      ]
    },
    {
      heading: 'Grouped Alerts in This Window',
      paragraphs: [
        'The same alerts, grouped by what changed. One window can hold several unrelated changes.'
      ]
    }
  ]
};

const SETTINGS = {
  title: 'Settings',
  sections: [
    {
      heading: 'Purpose',
      paragraphs: [
        'These settings decide when a window opens, how long a group stays open, and how often summary messages are sent.'
      ]
    },
    {
      // The "not retroactive" sentence is on the page itself, in the notice at
      // the top. It is not repeated here.
      heading: 'Before You Change a Setting',
      paragraphs: [
        'A smaller threshold opens more windows and sends fewer individual messages. A larger threshold does the opposite. These settings do not change what the system detects. They change how alerts are grouped and sent.'
      ]
    }
  ]
};

/** One entry for each screen in this section. */
export const HELP_CONTENT = {
  overview: OVERVIEW,
  changeEvents: CHANGE_EVENTS,
  changeEventDetail: CHANGE_EVENT_DETAIL,
  siteIncidents: SITE_INCIDENTS,
  siteIncidentDetail: SITE_INCIDENT_DETAIL,
  settings: SETTINGS
};

export default HELP_CONTENT;
